import type { ReportDB } from '@creator/sdk/modules/report/report.model';
import { orderBy, type QueryConstraint, where } from 'firebase/firestore/lite';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { createCollectionAtomFamily, createLoadMoreAtom } from '../common/collectionAtom';
import { setMultiple, setSingle } from '../common/common';
import { createMapAtom } from '../common/mapAtom';
import { jotaiStore } from '../store';

// Core atoms
const reportsAtom = createMapAtom<ReportDB>({
    initialValue: {},
    debugLabel: 'reportsAtom',
});

export const reportAtomFamily = atomFamily((reportId: string | undefined) => {
    const derivedAtom = atom<ReportDB | undefined>(get => (reportId ? get(reportsAtom)[reportId] : undefined));
    derivedAtom.debugLabel = `reportAtomFamily/${reportId}`;
    return derivedAtom;
});

export const getReportByIdAtom = atom(get => (reportId: string | undefined): ReportDB | undefined => {
    if (!reportId) return;
    return get(reportsAtom)[reportId];
});

// Collection state for pagination
export interface ReportCollectionCompositeKeyParams {
    tokenName: string;
    status?: string; // 'New', 'Done', 'Rejected'
    type?: string; // 'User', 'Post', 'Comment', 'Community'
    fetchLimit?: number;
}

export function generateReportCollectionKey(params: ReportCollectionCompositeKeyParams): string {
    let key = `reports_${params.tokenName}`;
    if (params.status) key += `_status-${params.status}`;
    if (params.type) key += `_type-${params.type}`;
    return key;
}

export const reportCollectionAtomFamily = createCollectionAtomFamily<ReportCollectionCompositeKeyParams, ReportDB>({
    generateKey: generateReportCollectionKey,
    equalityCheck: (a, b) => generateReportCollectionKey(a) === generateReportCollectionKey(b),
    debugLabel: 'reportCollectionState',
});

// Query constraints for different report filters
const getQueryConstraints = (params: ReportCollectionCompositeKeyParams): QueryConstraint[] => {
    const { tokenName, status, type } = params;
    const constraints: QueryConstraint[] = [where('tokenName', '==', tokenName)];

    if (status && status !== 'all') {
        constraints.push(where('status', '==', status));
    }

    if (type && type !== 'all') {
        constraints.push(where('type', '==', type));
    }

    // Default ordering by creation date (newest first)
    constraints.push(orderBy('createdAt', 'desc'));

    return constraints;
};

export type LoadMoreReportsParams = ReportCollectionCompositeKeyParams & { fetchLimit?: number };

export const loadMoreReportsAtom = createLoadMoreAtom<ReportDB, ReportCollectionCompositeKeyParams>(reportCollectionAtomFamily, 'Report', getQueryConstraints, reportsAtom);

// Getters and setters
export function getReportsAtom() {
    return reportsAtom;
}

export function getReports() {
    return jotaiStore.get(reportsAtom);
}

export function setReport(report: ReportDB) {
    setSingle(report, reportsAtom);
}

export function setReports(reports: Record<string, ReportDB>) {
    setMultiple(reports, reportsAtom);
}

export function updateReport(reportId: string, content: Partial<ReportDB>) {
    if (!reportId) return;
    jotaiStore.set(reportsAtom, draft => {
        if (draft[reportId]) {
            Object.assign(draft[reportId], content);
        }
    });
}
